import * as ort from "onnxruntime-web/webgpu";

import { assetUrl, fetchBuffer } from "../assets";
import { quatConjugate, quatMul, quatRotate, quatToSixD } from "../math";
import type { EncoderManifest, PolicyManifest, ReferenceHorizon } from "../types";
import type { ReposeSim } from "./mujoco";

const HISTORY = 10;

class History {
  private readonly frames: Float32Array[] = [];

  constructor(private readonly width: number) {}

  clear(): void {
    this.frames.length = 0;
  }

  push(value: Float32Array): void {
    if (this.frames.length === 0) {
      for (let index = 0; index < HISTORY; index += 1) this.frames.push(value.slice());
      return;
    }
    this.frames.shift();
    this.frames.push(value.slice());
  }

  flat(): Float32Array {
    const out = new Float32Array(HISTORY * this.width);
    this.frames.forEach((frame, index) => out.set(frame, index * this.width));
    return out;
  }
}

export interface PolicyStep {
  action: Float32Array;
  attention: Float32Array;
}

export class BrowserPolicy {
  private readonly angular = new History(3);
  private readonly jointPos = new History(29);
  private readonly jointVel = new History(29);
  private readonly actions = new History(29);
  private readonly gravity = new History(3);
  private lastAction = new Float32Array(29);

  private constructor(
    private readonly encoderSession: ort.InferenceSession,
    private readonly policySession: ort.InferenceSession,
    private readonly encoder: EncoderManifest,
    private readonly manifest: PolicyManifest,
  ) {}

  static async create(
    encoder: EncoderManifest,
    policy: PolicyManifest,
    progress: (fraction: number, detail: string) => void,
  ): Promise<BrowserPolicy> {
    ort.env.wasm.numThreads = 1;
    ort.env.wasm.simd = true;
    ort.env.wasm.wasmPaths = assetUrl("runtime/ort/").href;
    progress(0.4, "Loading the two local ONNX graphs…");
    const [encoderBytes, policyBytes] = await Promise.all([
      fetchBuffer("models/theia-tiny.onnx"),
      fetchBuffer("models/policy.onnx"),
    ]);
    progress(0.66, "Compiling the vision encoder…");
    const encoderSession = await BrowserPolicy.createSession(encoderBytes);
    progress(0.79, "Compiling the low-level policy…");
    const policySession = await BrowserPolicy.createSession(policyBytes);
    progress(0.96, "Policy runtime ready");
    return new BrowserPolicy(encoderSession, policySession, encoder, policy);
  }

  private static async createSession(model: ArrayBuffer): Promise<ort.InferenceSession> {
    const gpu = "gpu" in navigator ? navigator.gpu : null;
    const adapter = gpu ? await gpu.requestAdapter() : null;
    if (adapter) {
      try {
        return await ort.InferenceSession.create(model, {
          executionProviders: ["webgpu"],
          graphOptimizationLevel: "all",
        });
      } catch (error) {
        console.warn("WebGPU session failed; falling back to single-thread WASM", error);
      }
    }
    return ort.InferenceSession.create(model, {
      executionProviders: ["wasm"],
      graphOptimizationLevel: "all",
    });
  }

  reset(sim: ReposeSim): void {
    this.angular.clear();
    this.jointPos.clear();
    this.jointVel.clear();
    this.actions.clear();
    this.gravity.clear();
    this.lastAction = new Float32Array(29);
    this.captureState(sim);
  }

  cameraOmega(sim: ReposeSim): number {
    const gyro = sim.data.sensor("imu_ang_vel");
    let value = Math.hypot(Number(gyro.data[0]), Number(gyro.data[1]), Number(gyro.data[2]));
    gyro.delete();
    const waist = [12, 13, 14];
    const velocity = sim.jointVelocity();
    for (const index of waist) value += Math.abs(velocity[index] ?? 0);
    return value;
  }

  async infer(
    sim: ReposeSim,
    headPixels: Uint8Array,
    reference: ReferenceHorizon,
    targetColor: number,
  ): Promise<PolicyStep> {
    this.captureState(sim);
    const bgr = this.toBgr(headPixels);
    const enc = await this.encoderSession.run({
      [this.encoder.input.name]: new ort.Tensor("uint8", bgr, [1, this.encoder.input.height, this.encoder.input.width, 3]),
    });
    const patches = enc.patch_tokens;
    const cls = enc.cls_token;
    if (!patches || !cls) throw new Error("vision encoder did not return patch_tokens and cls_token");

    const feeds: Record<string, ort.Tensor> = {
      tokenizer: new ort.Tensor("float32", this.tokenizer(sim, reference), [1, 640]),
      policy: new ort.Tensor("float32", this.policyObservation(), [1, 930]),
      augmentation: new ort.Tensor("float32", this.augmentation(reference), [1, 18]),
      kv_tokens__img_tokens: patches,
      q_task_cmd: new ort.Tensor("float32", this.oneHot(targetColor), [1, 6]),
      q_proprio: new ort.Tensor("float32", this.proprio(sim), [1, 64]),
      q_cls: cls,
    };
    const output = await this.policySession.run(feeds);
    if (!output.actions || !output.attn) throw new Error("policy did not return actions and attn");
    this.lastAction = Float32Array.from(output.actions.data as Float32Array);
    return {
      action: this.lastAction,
      attention: Float32Array.from(output.attn.data as Float32Array),
    };
  }

  private captureState(sim: ReposeSim): void {
    const q = sim.jointPosition();
    const qRel = new Float32Array(29);
    for (let i = 0; i < q.length; i += 1) qRel[i] = (q[i] ?? 0) - (this.manifest.action.default_joint_pos[i] ?? 0);
    const qv = sim.jointVelocity();
    const root = sim.rootPose();
    const projected = Float32Array.from(quatRotate(quatConjugate(root.quat), [0, 0, -1]));
    const gyro = sim.data.sensor("imu_ang_vel");
    const angular = Float32Array.from([gyro.data[0], gyro.data[1], gyro.data[2]]);
    gyro.delete();
    this.angular.push(angular);
    this.jointPos.push(qRel);
    this.jointVel.push(qv);
    this.actions.push(this.lastAction);
    this.gravity.push(projected);
  }

  private policyObservation(): Float32Array {
    const out = new Float32Array(930);
    let offset = 0;
    for (const value of [
      this.angular.flat(),
      this.jointPos.flat(),
      this.jointVel.flat(),
      this.actions.flat(),
      this.gravity.flat(),
    ]) {
      out.set(value, offset);
      offset += value.length;
    }
    return out;
  }

  private proprio(sim: ReposeSim): Float32Array {
    const out = new Float32Array(64);
    const root = sim.rootPose();
    out.set(quatRotate(quatConjugate(root.quat), [0, 0, -1]), 0);
    const gyro = sim.data.sensor("imu_ang_vel");
    out.set(gyro.data as Float64Array, 3);
    gyro.delete();
    const q = sim.jointPosition();
    for (let i = 0; i < q.length; i += 1) out[6 + i] = (q[i] ?? 0) - (this.manifest.action.default_joint_pos[i] ?? 0);
    out.set(sim.jointVelocity(), 35);
    return out;
  }

  private tokenizer(sim: ReposeSim, reference: ReferenceHorizon): Float32Array {
    const out = new Float32Array(640);
    const chopped = new Float32Array(580);
    chopped.set(reference.jointPos, 0);
    chopped.set(reference.jointVel, reference.jointPos.length);
    const robotQuat = sim.rootPose().quat;
    const inv = quatConjugate(robotQuat);
    for (let frame = 0; frame < 10; frame += 1) {
      out.set(chopped.subarray(frame * 58, frame * 58 + 58), frame * 64);
      const ref = reference.pelvisQuat.subarray(frame * 4, frame * 4 + 4);
      out.set(quatToSixD(quatMul(inv, ref)), frame * 64 + 58);
    }
    return out;
  }

  private augmentation(reference: ReferenceHorizon): Float32Array {
    const out = new Float32Array(18);
    out.set(reference.contact, 0);
    out.set(reference.rootLinVel, 12);
    out.set(reference.rootAngVel, 15);
    return out;
  }

  private oneHot(color: number): Float32Array {
    const out = new Float32Array(6);
    if (color >= 0 && color < 6) out[color] = 1;
    return out;
  }

  private toBgr(rgbaBottomUp: Uint8Array): Uint8Array {
    const { height, width } = this.encoder.input;
    const out = new Uint8Array(height * width * 3);
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const source = ((height - 1 - y) * width + x) * 4;
        const target = (y * width + x) * 3;
        out[target] = rgbaBottomUp[source + 2] ?? 0;
        out[target + 1] = rgbaBottomUp[source + 1] ?? 0;
        out[target + 2] = rgbaBottomUp[source] ?? 0;
      }
    }
    return out;
  }
}
